import { Link } from 'react-router-dom';
import styled from 'styled-components';

const Main = styled.main`
  width: 100%;
  max-width: 1620px;
  margin: auto;
  padding: 70px 80px;

  @media (max-width: 900px) {
    padding: 40px 24px;
  }

  @media (max-width: 600px) {
    padding: 24px 16px;
  }
`;

const HeroSection = styled.section`
  width: 100%;
  background: #f7f8ed;
  overflow: hidden;
  border-radius: 40px 40px 40px 0px;
`;

const BannerImage = styled.img`
  display: block;
  width: 100%;
  max-height: 430px;
  object-fit: cover;
`;

const PageContainer = styled.div<{ isMobile: boolean; isTablet: boolean }>`
  width: 100%;
  max-width: 1180px;
  margin: 0 auto;
  padding: ${(p) => (p.isMobile ? '28px 16px 56px' : p.isTablet ? '42px 32px 72px' : '48px 40px 90px')};
`;

const ActionPanel = styled(Link)`
  width: min(760px, 100%);
  min-height: 96px;
  margin: 0 auto;
  padding: 16px 24px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 8px;
  text-align: center;
  color: ${(p) => p.theme.colors.light};
  background: ${(p) => p.theme.colors.primary500};
  text-decoration: none;
  border-radius: 8px;

  strong {
    color: ${(p) => p.theme.colors.light};
    font-size: clamp(24px, 3vw, 34px);
    font-weight: 600;
    line-height: 1.2;
  }
`;

const ContentSection = styled.section`
  margin-top: 64px;

  &:first-of-type {
    margin-top: 0;
  }
`;

const Eyebrow = styled.div`
  margin-bottom: 12px;
  color: ${(p) => p.theme.colors.secondary500};
  font-size: ${(p) => p.theme.fontSizes.xl};
  font-weight: 700;
  overflow-wrap: anywhere;

  @media (max-width: 600px) {
    font-size: ${(p) => p.theme.fontSizes.lg};
  }
`;

const Title = styled.h1`
  margin-bottom: 24px;
  color: ${(p) => p.theme.colors.primary500};
  font-size: ${(p) => p.theme.fontSizes.lg};
  font-weight: 700;
  line-height: 1.15;
`;

const SectionTitle = styled.h2`
  margin-bottom: 20px;
  color: ${(p) => p.theme.colors.primary500};
  font-size: ${(p) => p.theme.fontSizes.lg};
  font-weight: 700;
  line-height: 1.25;
`;

const BodyText = styled.p`
  margin-bottom: 18px;
  color: ${(p) => p.theme.colors.gray400};
  font-size: ${(p) => p.theme.fontSizes.base};
  line-height: 1.7;
`;

const FeatureGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 18px;
  margin-top: 30px;
`;

const FeatureItem = styled.div`
  min-height: 132px;
  padding: 22px;
  background: ${(p) => p.theme.colors.light};
  border: 1px solid ${(p) => p.theme.colors.gray100};
  border-top: 5px solid ${(p) => p.theme.colors.secondary500};
  border-radius: 8px;
`;

const FeatureValue = styled.div`
  margin-bottom: 12px;
  color: ${(p) => p.theme.colors.primary500};
  font-size: ${(p) => p.theme.fontSizes.md};
  font-weight: 700;
  line-height: 1.3;
`;

const FeatureLabel = styled.div`
  color: ${(p) => p.theme.colors.gray400};
  font-size: ${(p) => p.theme.fontSizes.base};
  line-height: 1.55;
`;

const SplitSection = styled.section<{ isTablet: boolean }>`
  display: grid;
  grid-template-columns: ${(p) => (p.isTablet ? '1fr' : '0.95fr 1.05fr')};
  gap: ${(p) => (p.isTablet ? '24px' : '42px')};
  align-items: start;
  margin-top: 64px;
`;

const TextColumn = styled.div``;

const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
  gap: 12px;
`;

const InfoItem = styled.div`
  min-height: 84px;
  padding: 18px 20px;
  display: flex;
  align-items: center;
  color: ${(p) => p.theme.colors.gray500};
  background: ${(p) => p.theme.colors.light};
  border: 1px solid ${(p) => p.theme.colors.gray100};
  border-left: 5px solid ${(p) => p.theme.colors.primary300};
  border-radius: 8px;
  line-height: 1.55;
`;

const ContactLink = styled.a`
  color: ${(p) => p.theme.colors.primary500};
  font-weight: 700;
  text-decoration: underline;
`;

const TabList = styled.div`
  display: flex;
  gap: 24px;
  border-bottom: 1px solid ${(p) => p.theme.colors.gray100};
  margin-bottom: 40px;

  @media (max-width: 600px) {
    gap: 12px;
  }
`;

const TabButton = styled.button<{ active: boolean }>`
  appearance: none;
  border: 0;
  border-bottom: 4px solid ${(p) => (p.active ? p.theme.colors.secondary500 : 'transparent')};
  background: transparent;
  color: ${(p) => (p.active ? p.theme.colors.primary500 : p.theme.colors.gray400)};
  cursor: pointer;
  font: inherit;
  font-size: ${(p) => p.theme.fontSizes.md};
  font-weight: 700;
  padding: 12px 4px;

  &:focus-visible {
    outline: 2px solid ${(p) => p.theme.colors.primary500};
    outline-offset: 3px;
  }

  @media (max-width: 600px) {
    flex: 1;
    font-size: ${(p) => p.theme.fontSizes.base};
  }
`;

const ConsultationLead = styled.div`
  max-width: 850px;
  margin-bottom: 40px;

  h1 {
    font-size: 42px;
    line-height: 1.2;
  }

  ${Eyebrow} {
    font-size: 22px;
    line-height: 1.4;
  }

  @media (max-width: 600px) {
    h1 {
      font-size: 30px;
    }
  }
`;

const OfficeButton = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 48px;
  padding: 10px 24px;
  color: ${(p) => p.theme.colors.light};
  background: ${(p) => p.theme.colors.primary500};
  border-radius: 4px;
  font-weight: 700;
  text-decoration: none;

  &:hover,
  &:focus-visible {
    text-decoration: underline;
  }
`;

const BenefitGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 18px;

  @media (max-width: 900px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

const BenefitItem = styled.article`
  padding: 24px 20px;
  border: 1px solid ${(p) => p.theme.colors.gray100};
  border-radius: 6px;

  img {
    display: block;
    width: 72px;
    height: 72px;
    object-fit: contain;
    margin-bottom: 16px;
  }

  h3 {
    color: ${(p) => p.theme.colors.primary500};
    font-size: ${(p) => p.theme.fontSizes.md};
    margin: 0 0 10px;
  }

  p {
    margin: 0;
    line-height: 1.6;
  }
`;

const AreaGrid = styled.ul`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 0 24px;
  padding: 0;
  margin: 28px 0 0;
  list-style: none;

  li {
    padding: 18px 0;
    border-top: 1px solid ${(p) => p.theme.colors.gray100};
  }

  strong {
    display: block;
    color: ${(p) => p.theme.colors.primary500};
    margin-bottom: 6px;
  }

  @media (max-width: 900px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

const ConditionsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 48px;

  h2 {
    margin-bottom: 16px;
  }

  ul {
    padding-left: 24px;
    line-height: 1.65;
  }

  li + li {
    margin-top: 12px;
  }

  @media (max-width: 700px) {
    grid-template-columns: 1fr;
    gap: 28px;
  }
`;

const StepsList = styled.ol`
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 20px;
  padding: 0;
  margin: 30px 0;
  list-style: none;
  counter-reset: step;

  li {
    padding: 18px 0;
    border-top: 4px solid ${(p) => p.theme.colors.secondary500};
    counter-increment: step;
  }

  li::before {
    content: counter(step, decimal-leading-zero);
    display: block;
    margin-bottom: 14px;
    color: ${(p) => p.theme.colors.secondary500};
    font-size: ${(p) => p.theme.fontSizes.md};
    font-weight: 700;
  }

  strong {
    display: block;
    margin-bottom: 8px;
    color: ${(p) => p.theme.colors.primary500};
  }

  @media (max-width: 900px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

export {
  ActionPanel,
  AreaGrid,
  BannerImage,
  BenefitGrid,
  BenefitItem,
  BodyText,
  ConditionsGrid,
  ContactLink,
  ConsultationLead,
  ContentSection,
  Eyebrow,
  FeatureGrid,
  FeatureItem,
  FeatureLabel,
  FeatureValue,
  HeroSection,
  InfoGrid,
  InfoItem,
  Main,
  OfficeButton,
  PageContainer,
  SectionTitle,
  SplitSection,
  StepsList,
  TabButton,
  TabList,
  TextColumn,
  Title,
};
